import type { UserStore } from '@/stores/userStore';
import { useUserStore } from '@/stores/userStore';
import { RouteRecordRaw } from 'vue-router';

export const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    beforeEnter: async (to, from, next) => {
      const store: UserStore = useUserStore();
      let { userToken, userEmail, isLogin } = store;

      // 토큰이 있고 이메일이 있는 경우
      if (userToken && userEmail) {
        // Access token이 만료되었지만 refresh token이 유효한 경우 자동 갱신 시도
        if (store.isAccessTokenExpired() && !store.isRefreshTokenExpired()) {
          console.log('🔄 [Router Guard] Access token 만료됨, 자동 갱신 시도');
          try {
            const refreshSuccess = await store.refreshAccessToken();
            if (refreshSuccess) {
              console.log('✅ [Router Guard] 토큰 갱신 성공, 페이지 진행');
              next();
            } else {
              console.log('❌ [Router Guard] 토큰 갱신 실패, 로그인 페이지로 이동');
              if (to.path !== '/login') {
                const fullPath = to.fullPath;
                const encodedRedirectPath = encodeURIComponent(fullPath);
                next({ path: '/login', query: { redirect: encodedRedirectPath } });
              } else {
                next();
              }
            }
          } catch (error) {
            console.error('❌ [Router Guard] 토큰 갱신 중 오류:', error);
            if (to.path !== '/login') {
              const fullPath = to.fullPath;
              const encodedRedirectPath = encodeURIComponent(fullPath);
              next({ path: '/login', query: { redirect: encodedRedirectPath } });
            } else {
              next();
            }
          }
        } else {
          // Access token이 유효한 경우 바로 진행
          next();
        }
      } else {
        // 토큰이나 이메일이 없는 경우
        if (to.path !== '/login') {
          // 로그인 페이지로 리다이렉트할 때 원래 URL을 쿼리 파라미터로 전달
          // URL 인코딩을 통해 쿼리 파라미터가 있는 URL을 안전하게 전달
          const fullPath = to.fullPath;
          const encodedRedirectPath = encodeURIComponent(fullPath);
          next({ path: '/login', query: { redirect: encodedRedirectPath } });
        } else {
          next();
        }
      }
    },
    component: () => import('@/layouts/default.vue'),
    children: [
      {
        path: '/',
        redirect: '/dashboard',
      },
      {
        path: 'dashboard',
        component: () => import('@/pages/main-index.vue'),
      },
      {
        path: 'account-settings',
        component: () => import('@/pages/header/account-settings.vue'),
      },
      {
        path: 'typography',
        component: () => import('@/pages/etc/typography.vue'),
      },
      {
        path: 'icons',
        component: () => import('@/pages/etc/icons.vue'),
      },
      {
        path: 'cards',
        component: () => import('@/pages/etc/cards.vue'),
      },
      {
        path: 'tables',
        component: () => import('@/pages/etc/tables.vue'),
      },
      {
        path: 'form-layouts',
        component: () => import('@/pages/etc/form-layouts.vue'),
      },
      {
        path: 'faq',
        component: () => import('@/pages/navi-volunteer/faq/index.vue'),
      },
      {
        path: 'qna-dialog',
        component: () => import('@/pages/navi-volunteer/qna/dialog/index.vue'),
      },
      {
        path: 'jt-volunteer',
        component: () => import('@/pages/navi-volunteer/jt-volunteer.vue'),
      },
      {
        path: 'jt-volunteer-form',
        name: 'jt-volunteer-form',
        component: () => import('@/pages/navi-volunteer/jt-volunteer-form.vue'),
      },
      {
        path: 'jt-volunteer-apply',
        component: () => import('@/pages/navi-volunteer/jt-volunteer-apply.vue'),
      },
      {
        path: 'jt-volunteer-edu',
        component: () => import('@/pages/navi-volunteer/jt-volunteer-edu.vue'),
      },
      {
        path: 'jt-volunteer-detail',
        component: () => import('@/pages/navi-volunteer/jt-volunteer-detail.vue'),
      },
      {
        path: 'jt-volunteer-apply-status',
        name: 'jt-volunteer-apply-status',
        component: () => import('@/pages/navi-volunteer/volunteer-status/jt-volunteer-apply-status.vue'),
      },
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/pages/header/profile/index.vue'),
      },
      {
        path: 'id-card',
        name: 'id-card',
        component: () => import('@/pages/header/id-card/index.vue'),
      },
      {
        path: 'jt-volunteer-voice',
        component: () => import('@/pages/navi-volunteer/jt-volunteer-voice.vue'),
      },
      {
        path: 'jt-department-info',
        component: () => import('@/pages/navi-volunteer/jt-department-info.vue'),
      },
      {
        path: 'jt-qna-list',
        component: () => import('@/pages/navi-volunteer/jt-qna-list.vue'),
      },
      {
        path: 'jt-faq',
        component: () => import('@/pages/navi-volunteer/faq/index.vue'),
      },
      {
        path: 'jt-board-reply',
        component: () => import('@/pages/navi-inquiry-manage/jt-board-reply-list.vue'),
      },
      {
        path: 'jt-global-reply',
        component: () => import('@/pages/navi-inquiry-manage/jt-global-board-reply.vue'),
      },
      {
        path: 'jt-board-reply-form',
        component: () => import('@/pages/navi-inquiry-manage/jt-board-reply-form.vue'),
        props: route => ({ boardId: route.query.boardId }),
      },
      {
        path: 'jt-board-reply-modify-form',
        component: () => import('@/pages/navi-inquiry-manage/jt-board-reply-modify-form.vue'),
      },
      {
        path: 'jt-system-manage',
        component: () => import('@/pages/navi-inquiry-manage/jt-system-manage.vue'),
      },
      {
        path: 'jt-system-manage-form',
        component: () => import('@/pages/navi-inquiry-manage/jt-system-manage-form.vue'),
        props: route => ({ boardId: route.query.boardId }),
      },
      {
        path: 'jt-system-manage-modify-form',
        component: () => import('@/pages/navi-inquiry-manage/jt-system-manage-modify-form.vue'),
      },
      {
        path: 'jt-message',
        name: 'jt-message',
        component: () => import('@/pages/navi-my-page/jt-message.vue'),
      },
      // --- 마이정토 재개편
      {
        path: 'jt-meet-enter',
        component: () => import('@/pages/navi-my-page/jt-meet-enter.vue'),
      },
      {
        path: 'jt-meet-notice',
        redirect: { path: '/jt-meet-enter', query: { tab: 'announce' } },
      },
      {
        path: 'jt-mind-share',
        name: 'jt-mind-share',
        component: () => import('@/pages/navi-my-page/jt-mind-share/index.vue'),
      },
      {
        path: 'jt-mind-share/member/:memberId/:memberName',
        name: 'mind-share-member',
        component: () => import('@/pages/navi-my-page/jt-mind-share-detail/index.vue'),
        props: true,
      },
      {
        path: 'jt-inquire',
        component: () => import('@/pages/default-page.vue'),
      },
      {
        path: 'jt-user-portal',
        component: () => import('@/pages/navi-my-page/jt-user-portal/index.vue'),
      },
      {
        path: 'jt-user-portal/training-history',
        component: () => import('@/pages/navi-my-page/jt-user-portal/sub-pages/training/index.vue'),
      },
      {
        path: 'jt-user-portal/education-history',
        component: () => import('@/pages/navi-my-page/jt-user-portal/sub-pages/education/index.vue'),
      },
      {
        path: 'jt-user-portal/qualification-history',
        component: () => import('@/pages/navi-my-page/jt-user-portal/sub-pages/qualification/index.vue'),
      },
      {
        path: 'jt-user-portal/activity-history',
        component: () => import('@/pages/navi-my-page/jt-user-portal/sub-pages/activity/index.vue'),
      },
      {
        path: 'jt-user-portal/activity-application',
        component: () => import('@/pages/navi-my-page/jt-user-portal/sub-pages/activity/application.vue'),
      },
      {
        path: 'jt-user-portal/meeting-attendance',
        component: () => import('@/pages/navi-my-page/jt-user-portal/sub-pages/meeting/index.vue'),
      },
      {
        path: 'jt-invite',
        component: () => import('@/pages/navi-my-page/jt-invite/index.vue'),
      },
      {
        path: 'jt-invite-input',
        component: () => import('@/pages/navi-my-page/jt-invite-input.vue'),
      },
      {
        path: 'jt-invite-guest-input',
        component: () => import('@/pages/navi-my-page/jt-invite-guest-input.vue'),
      },
      {
        path: 'jt-invite-input-process',
        component: () => import('@/pages/navi-my-page/jt-invite-input-process.vue'),
      },
      {
        path: 'jt-schedule',
        component: () => import('@/pages/navi-shared-info/jt-schedule.vue'),
      },
      {
        path: 'jt-vote',
        component: () => import('@/pages/navi-shared-info/jt-vote.vue'),
      },
      {
        path: 'jt-recommend',
        component: () => import('@/pages/navi-shared-info/jt-recommand.vue'),
      },
      {
        path: 'jt-notice',
        component: () => import('@/pages/navi-shared-info/jt-notice-list.vue'),
      },
      {
        path: 'jt-notice-form',
        component: () => import('@/pages/navi-shared-info/jt-notice-form.vue'),
        meta: { fullWidth: true },
      },
      {
        path: 'jt-notice-detail',
        component: () => import('@/pages/navi-shared-info/jt-notice-detail.vue'),
      },
      {
        path: 'jt-notice-admin',
        component: () => import('@/pages/navi-shared-info/jt-notice-admin.vue'),
        meta: { fullWidth: true },
      },
      {
        path: 'jt-inter-dharma-talk',
        component: () => import('@/pages/my-jungto/jt-inter-dharma-talk/index.vue'),
      },
      {
        path: 'jt-inter-meditation',
        component: () => import('@/pages/default-page.vue'),
        // component: () => import('@/pages/navi-my-jungto/jt-inter-meditation.vue'),
      },
      {
        path: 'jt-dharma-talk-participate',
        component: () => import('@/pages/navi-dharma-meet/jt-dharma-talk-participate/index-participate.vue'),
      },
      {
        path: 'jt-dharma-talk-question',
        component: () => import('@/pages/navi-dharma-meet/jt-dharma-talk-question/suheng-question-intro.vue'),
      },
      {
        path: 'jt-dharma-talk-question/suheng-question',
        component: () => import('@/pages/navi-dharma-meet/jt-dharma-talk-question/suheng-question.vue'),
      },
      {
        path: 'jt-dharma-talk-question/suheng-question-complete',
        component: () => import('@/pages/navi-dharma-meet/jt-dharma-talk-question/suheng-question-complete.vue'),
      },
      {
        path: 'jt-dharma-talk-question/suheng-question-update',
        component: () => import('@/pages/navi-dharma-meet/jt-dharma-talk-question/suheng-question-update.vue'),
      },
      {
        path: 'jt-home-manage',
        component: () => import('@/pages/navi-inquiry-manage/jt-home-manage.vue'),
      },
      {
        path: 'debug',
        component: () => import('@/pages/debug/debug.vue'),
      },
    ],
  },
  {
    path: '/',
    component: () => import('@/layouts/blank.vue'),
    children: [
      {
        path: 'login',
        component: () => import('@/pages/login.vue'),
      },
      {
        path: 'non-member-redirect',
        component: () => import('@/pages/non-member-redirect.vue'),
      },
    ],
  },
  // 에러 페이지들을 별도로 정의
  {
    path: '/404',
    component: () => import('@/pages/[...error].vue'),
    props: true,
  },
  {
    path: '/500',
    component: () => import('@/pages/[...error].vue'),
    props: true,
  },
  {
    path: '/403',
    component: () => import('@/pages/[...error].vue'),
    props: true,
  },
  {
    path: '/401',
    component: () => import('@/pages/[...error].vue'),
    props: true,
  },
  {
    path: '/400',
    component: () => import('@/pages/[...error].vue'),
    props: true,
  },
  // Sentry 연결 테스트용 (인증 불필요, 연동 확인 후 제거 권장)
  {
    path: '/sentry-test',
    component: () => import('@/pages/sentry-test.vue'),
  },
  // 마지막에 캐치올 라우트 (정의되지 않은 모든 경로)
  {
    path: '/:pathMatch(.*)*',
    component: () => import('@/pages/[...error].vue'),
    props: true,
  },
];
